/* ───────────────────────────────────────────────────────────────────────────
 * lcsRecommendationLedger.js
 *
 * Forward-tracking "Proof Ledger Lite" derived deterministically from the
 * resolved decisionCard + metrics for every lease in the LCS cockpit.
 *
 * THIS IS NOT A HISTORICAL BACKTEST.
 *   - Ledger entries describe the CURRENT recommendation snapshot.
 *   - Outcomes are PENDING — observation begins at generatedAt.
 *   - We never claim "saved $X" or "validated historically".
 *   - "Tracking starts from this run" is the honest framing.
 *
 * The cockpit's resolveDecisionProfile() calls deriveLedger() and attaches
 * the result as `ledger` on the resolved profile. The right-rail UI renders
 * a compact ledger block inside DecisionCard.
 *
 * Schema (per lease):
 *   {
 *     leaseId, leaseName, generatedAt, ledgerLabel,
 *     modelRun: { freshness, forecastAgeDays, oil/gas/water snapshots, qualityFlags },
 *     recommendationSnapshot: { status, primaryMove, priority, confidence, score,
 *                               decisionLine, moneyLever, upside, caution, evidence },
 *     actionPlan: [ { title, ownerRole, urgency, checklist, expectedMetricToImprove,
 *                     successCriteria, recheckAfterDays } ],
 *     actionStatus: { status, reason },
 *     actualOutcomeWindow: { status, observationStart, recheckAfterDays,
 *                            metricsToWatch, expectedDirection },
 *     roiEstimate: { valueRange, confidence, primaryLever, scenario, caveat },
 *   }
 * ──────────────────────────────────────────────────────────────────────── */

// ── Helpers ────────────────────────────────────────────────────────────────

function _todayIso() {
  // YYYY-MM-DD only, deterministic across timezones for ledger reporting.
  return new Date().toISOString().slice(0, 10);
}

function _fmtNum(v, d = 1) {
  if (typeof v !== "number" || !Number.isFinite(v)) return null;
  const s = v.toFixed(d).replace(/\.0+$/, "");
  return s;
}

function _fmtPct(v) {
  if (typeof v !== "number" || !Number.isFinite(v)) return null;
  const sign = v >= 0 ? "+" : "";
  return `${sign}${(v * 100).toFixed(0)}%`;
}

function _streamSnapshot(s, unit) {
  if (!s) return null;
  const out = {};
  if (s.p50 != null) out.p50 = `${_fmtNum(s.p50, 2)} ${unit}`;
  if (s.p95 != null) out.p95 = `${_fmtNum(s.p95, 2)} ${unit}`;
  if (s.mape != null) out.mape = `${(s.mape * 100).toFixed(0)}%`;
  if (s.trend7v30 != null) out.trend = `${_fmtPct(s.trend7v30)} 7v30`;
  return Object.keys(out).length ? out : null;
}

function _buildModelRun(metrics, card) {
  if (!metrics) {
    return {
      freshness: "UNKNOWN",
      forecastAgeDays: null,
      oil: null,
      gas: null,
      water: null,
      qualityFlags: ["No live metrics in payload"],
    };
  }
  const age = metrics.ageDays;
  const freshness =
    typeof age !== "number" ? "UNKNOWN" :
    age <= 7 ? "FRESH" :
    age <= 30 ? "SLIGHTLY STALE" :
    "STALE";

  // Pull caution flags through as quality signals
  const qualityFlags = (card?.cautionFlags || []).slice(0, 4);

  return {
    freshness,
    forecastAgeDays: typeof age === "number" ? age : null,
    oil:   _streamSnapshot(metrics.streams?.Oil,   "bbl/d"),
    gas:   _streamSnapshot(metrics.streams?.Gas,   "mcf/d"),
    water: _streamSnapshot(metrics.streams?.Water, "bbl/d"),
    qualityFlags,
  };
}

// ── Status mapping ────────────────────────────────────────────────────────

function _mapActionStatus(card) {
  const status = String(card?.status || "").toUpperCase();
  if (/DATA QUALITY|MEASUREMENT/.test(status)) {
    return { status: "BLOCKED BY DATA QUALITY", reason: "Recent reads contaminated; restore data integrity first.", recheckAfterDays: 7 };
  }
  if (/DECISION REQUIRED/.test(status)) {
    return { status: "PENDING DECISION", reason: "Operator decision overdue; binary call required.", recheckAfterDays: 7 };
  }
  if (/INVESTIGATE/.test(status)) {
    return { status: "PENDING FIELD REVIEW", reason: "Field diagnostic required to validate root cause.", recheckAfterDays: 7 };
  }
  if (/POST-WORKOVER/.test(status)) {
    return { status: "PENDING STABILIZATION", reason: "Recently worked-over well still re-baselining.", recheckAfterDays: 30 };
  }
  if (/POST-RIG/.test(status)) {
    return { status: "PENDING STABILIZATION", reason: "Post-rig restart still characterizing steady-state.", recheckAfterDays: 14 };
  }
  if (/GAS HANDLING/.test(status)) {
    return { status: "PENDING FIELD REVIEW", reason: "Surface gas-handling defect needs verification.", recheckAfterDays: 7 };
  }
  if (/RESOLVED|OPTIMIZATION CANDIDATE/.test(status)) {
    return { status: "PROPOSED", reason: "Awaiting field/ops confirmation to commit.", recheckAfterDays: 14 };
  }
  if (/WATCH \(NO DATA\)/.test(status)) {
    return { status: "BLOCKED BY DATA QUALITY", reason: "No production series and no notes — verify asset class.", recheckAfterDays: 30 };
  }
  if (/WATCH/.test(status) || /MONITOR/.test(status)) {
    return { status: "NO ACTION / MONITOR", reason: "No active issue; monthly review.", recheckAfterDays: 30 };
  }
  return { status: "PROPOSED", reason: "Awaiting field/ops confirmation.", recheckAfterDays: 14 };
}

// ── Owner / urgency derivation ────────────────────────────────────────────

function _ownerFor(actionTitle, status) {
  const t = String(actionTitle || "").toLowerCase();
  if (/meter|gauge|tech|inspect|repair|workover|pull |belt|separator|back-pressure|drive train/.test(t)) return "Pumper / Field Tech";
  if (/swd|disposal|loe|chemical|tickets|ekon|econ|deck|capex|baseline|economics|capital/.test(t)) return "Office / Ops";
  if (/forecast|backfill|model|reconcile/.test(t)) return "Data / Analyst";
  if (/escalate|integrity|workover decision|formal/.test(t)) return "Operations Lead";
  if (/verify|confirm|classification|asset/.test(t)) return "Operations Lead";
  return "Pumper / Field Tech";
}

function _urgencyFor(actionTitle, status) {
  const t = String(actionTitle || "").toLowerCase();
  if (/today/.test(t)) return "Today";
  if (/now/.test(t)) return "Today";
  if (/escalate|workover decision|hold optimization/.test(t)) return "This week";
  if (/this week|next field route|dispatch/.test(t)) return "This week";
  if (/14|month|monthly|quarterly|re-evaluate at/.test(t)) return "Within 30d";
  return "Next field route";
}

function _checklistFor(actionTitle) {
  const t = String(actionTitle || "").toLowerCase();
  if (/separator|back-pressure|two-phase|knockout/.test(t)) {
    return ["Walk separator / knockout / back-pressure valves", "Confirm pressures vs. design", "Log readings in daily note"];
  }
  if (/meter/.test(t)) {
    return ["Inspect meter wiring + power", "Confirm signal at gauge tank", "Validate reading against tank strap"];
  }
  if (/belt|drive train/.test(t)) {
    return ["Pull belt assembly", "Inspect sheave alignment + wear", "Replace belt set if needed"];
  }
  if (/flow.line|integrity/.test(t)) {
    return ["Walk 2R flow line", "Pressure test repaired joint", "Document stable run for 7 days"];
  }
  if (/workover|pump.*replac|bottom/.test(t)) {
    return ["Confirm rod/pump configuration logged", "Schedule fluid-level survey at 14d", "Track post-workover oil rate"];
  }
  if (/swd|disposal|tickets/.test(t)) {
    return ["Pull last 60d disposal tickets", "Confirm hauling rate vs SWD contract", "Stress-test EconModal at p95 water"];
  }
  if (/chemical/.test(t)) {
    return ["Audit chemical program against current oil rate", "Trim if over-spec", "Verify treatment frequency"];
  }
  if (/asset class|classification|verify lease/.test(t)) {
    return ["Confirm lease class (producer/injection/inactive)", "Capture disposal contract context if injection", "Update bundle metadata"];
  }
  if (/backfill/.test(t)) {
    return ["Trigger pd_backfill for this lease", "Verify createdAt is current", "Re-rate after fresh forecast lands"];
  }
  return ["Execute action", "Capture outcome in daily note", "Recheck on schedule"];
}

function _expectedMetricFor(actionTitle, status, card) {
  const t = String(actionTitle || "").toLowerCase();
  if (/meter|measurement/.test(t)) return "Stable daily measurement (no 'meter is asleep' notes)";
  if (/separator|back-pressure/.test(t)) return "Gas trend remains stable; water p95 does not re-expand";
  if (/belt|drive train/.test(t)) return "Wickham well uptime restored; no recurring 'belts burn' notes";
  if (/flow.line/.test(t)) return "Zero new flow-line leak notes for 7+ days";
  if (/workover|pump.*replac/.test(t)) return "Post-workover oil rate stabilizes at or above pre-workover p50";
  if (/swd|disposal|tickets/.test(t)) return "EconModal updated with verified SWD assumptions";
  if (/chemical/.test(t)) return "Chemical spend right-sized to current oil rate";
  if (/integrity|workover decision/.test(t)) return "Decision logged: workover scheduled or formal retirement";
  return "Outcome captured in daily note within recheck window";
}

function _successCriteriaFor(actionTitle, status) {
  const t = String(actionTitle || "").toLowerCase();
  if (/meter/.test(t)) return "7 consecutive clean measurement days";
  if (/separator|back-pressure/.test(t)) return "7 clean days with stable gas band and no repeat separator note";
  if (/belt|drive train/.test(t)) return "14 days of continuous uptime on Wickham #2";
  if (/flow.line/.test(t)) return "No 4th leak event within 7 days";
  if (/workover|pump.*replac/.test(t)) return "Oil p50 holds within ±15% of pre-workover baseline at +30d";
  if (/swd|disposal/.test(t)) return "Verified SWD price loaded into EconModal; pro-forma re-run";
  if (/chemical/.test(t)) return "Chemical OPEX reduced or held flat at current oil rate";
  return "Outcome documented and verified within recheck window";
}

function _recheckForAction(actionTitle, defaultDays) {
  const t = String(actionTitle || "").toLowerCase();
  if (/today|dispatch.*today/.test(t)) return 7;
  if (/this week|next field/.test(t)) return 7;
  if (/14|2.weeks|fortnight/.test(t)) return 14;
  if (/30|month|quarterly|re-evaluate at/.test(t)) return 30;
  return defaultDays;
}

// ── Metrics-to-watch derivation ───────────────────────────────────────────

function _metricsToWatchFor(card) {
  const watch = new Set();
  const status = String(card?.status || "").toUpperCase();
  const flags = (card?.cautionFlags || []).map(s => String(s).toLowerCase()).join(" | ");
  const move = String(card?.primaryMove || "").toLowerCase();

  if (/meter intermittent|meter asleep|meter/i.test(flags + " " + move)) watch.add("measurement restored");
  if (/separator|back-pressure|two-phase|knockout/i.test(flags + " " + move)) watch.add("gas trend stability");
  if (/water/i.test(flags) && /water mape|p95|water model noisy/i.test(flags)) watch.add("water p95");
  if (/belt/i.test(flags + " " + move)) watch.add("belt assembly status");
  if (/tubing leak|backside/i.test(flags + " " + move)) watch.add("operational status (still down vs back on)");
  if (/flow.line/i.test(flags + " " + move)) watch.add("recurring leak events");
  if (/post-workover|new pump/i.test(status + " " + move)) watch.add("new-pump output");
  if (/post-rig/i.test(status + " " + move)) watch.add("post-rig oil rate at +14d");
  if (/data quality/i.test(status)) watch.add("clean reads for 14 consecutive days");
  if (/optimization|resolved/i.test(status)) {
    watch.add("water disposal cost vs deck");
    watch.add("oil trend stability");
  }
  if (/no data/i.test(status)) {
    watch.add("asset classification verified");
  }
  if (watch.size === 0) {
    watch.add("oil trend");
    watch.add("water p95");
    watch.add("field notes");
  }
  return [...watch].slice(0, 4);
}

function _expectedDirectionFor(card) {
  const status = String(card?.status || "").toUpperCase();
  if (/WATCH \(NO DATA\)/.test(status))               return "Asset classification verified; backfill triggered if active, retired if not";
  if (/WATCH \(NEED CONTEXT\)/.test(status))          return "Field confirms whether shut-in is planned vs anomalous";
  if (/RESOLVED|OPTIMIZATION CANDIDATE/.test(status)) return "Cost lever validated; margin holds or improves";
  if (/DATA QUALITY/.test(status))                    return "Clean readings resume; volatility decays";
  if (/INVESTIGATE/.test(status))                     return "Root cause identified and logged";
  if (/DECISION REQUIRED/.test(status))               return "Decision recorded (workover scheduled or retirement filed)";
  if (/POST-WORKOVER/.test(status))                   return "New-pump rate stabilizes at or above pre-workover baseline";
  if (/POST-RIG/.test(status))                        return "Oil at +14d settles at >2 bbl/d (optimize) or <2 bbl/d (shut-in)";
  if (/GAS HANDLING/.test(status))                    return "Gas trend stable post-fix; surface defect not recurring";
  if (/MONITOR|WATCH/.test(status))                   return "Trend remains within model; no escalation";
  return "Outcome captured in next field cycle";
}

// ── ROI estimate ──────────────────────────────────────────────────────────

function _roiEstimate(card) {
  const lever = card?.moneyLever || {};
  const upside = lever.upside || "";
  const label  = lever.label  || "";
  const blob   = `${upside} ${label}`.toLowerCase();
  const cantSize =
    !upside ||
    /cannot size/.test(blob) ||
    /no active/.test(blob) ||
    /no production/.test(blob) ||
    /asset classification needed/.test(blob) ||
    /verify data state/.test(blob);

  return {
    valueRange: cantSize ? "Cannot size" : upside,
    confidence: cantSize ? "n/a" : (/directional/i.test(upside) ? "directional" : "estimate"),
    primaryLever: lever.label || "n/a",
    scenario: lever.label && !cantSize ? `${lever.label} — verify in EconModal` : "Resolve blocker before sizing",
    caveat: lever.caveat || "",
  };
}

// ── Main derivation ───────────────────────────────────────────────────────

export function deriveLedger({ leaseId, leaseName, card, metrics } = {}) {
  if (!card) return null;

  const map = _mapActionStatus(card);
  const today = _todayIso();
  const metricsToWatch = _metricsToWatchFor(card);
  const expectedDirection = _expectedDirectionFor(card);

  // Build action plan from card.nextActions, deriving owner/urgency/checklist
  // from the action text. Cap at 4 to keep the ledger compact.
  const actionPlan = (card.nextActions || []).slice(0, 4).map((title) => ({
    title,
    ownerRole: _ownerFor(title, card.status),
    urgency: _urgencyFor(title, card.status),
    checklist: _checklistFor(title),
    expectedMetricToImprove: _expectedMetricFor(title, card.status, card),
    successCriteria: _successCriteriaFor(title, card.status),
    recheckAfterDays: _recheckForAction(title, map.recheckAfterDays),
  }));

  return {
    leaseId,
    leaseName: leaseName || leaseId,
    generatedAt: today,
    ledgerLabel: "Tracking starts from this run",

    modelRun: _buildModelRun(metrics, card),

    recommendationSnapshot: {
      status: card.status,
      primaryMove: card.primaryMove,
      priority: card.priority || null,
      confidence: card.confidence || null,
      decisionLine: card.decisionLine,
      moneyLever: card.moneyLever?.label || null,
      upside: card.moneyLever?.upside || null,
      caution: (card.cautionFlags || []).slice(0, 5),
      evidence: (card.evidence || []).slice(0, 3),
    },

    actionPlan,

    actionStatus: { status: map.status, reason: map.reason },

    actualOutcomeWindow: {
      status: "PENDING",
      observationStart: today,
      recheckAfterDays: map.recheckAfterDays,
      metricsToWatch,
      expectedDirection,
    },

    roiEstimate: _roiEstimate(card),
  };
}
